// QuoteDraftPage — /procurement/quote. A supplier's quotation in, a draft
// purchase order out.
//
// ── THE ERRAND ───────────────────────────────────────────────────────────────
// A quote arrives by email. Somebody would otherwise re-key the vendor, seven
// lines, terms, a discount and two freight charges into /procurement/new. This
// reads it once and hands back a draft to check and submit.
//
// ── WHY THIS IS NOT PoScanPage ───────────────────────────────────────────────
// That screen ends in STOCK: the box is on the bench and the goods go on a shelf
// a moment later. This one ends in a DRAFT, because nothing has been ordered
// yet. The whole screen is therefore a review surface, and the button says
// "Create the draft" rather than "Confirm" — the buyer still has to submit it,
// where the vendor gates and the approval threshold live.
//
// What IS carried over unchanged, because a reviewer must not have to relearn
// it: the amber needs-review treatment (components/review), the mint cards
// (PoScanLineCard) kind-first with both classification axes required and
// neither defaulted, and the SKU grammar (Parts/skuGrammar).

import React from 'react';
import { Alert } from '../components/ui/Alert';
import { buttonClass } from '../components/ui/Button';
import { Panel } from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import { Checkbox } from '../components/ui/Checkbox';
import { Field } from '../components/ui/Field';
import { FileField } from '../components/ui/FileField';
import { Select } from '../components/ui/Select';
import { useHistory } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import HelpDrawer from '../components/HelpDrawer';
import PageLayout from '../components/ui/PageLayout';
import { useQuoteApi, reviewKey, quoteDraftPageUrl } from './quoteApi';
import type {
  QuoteParseResponse,
  QuoteExtraction,
  QuoteReviewEntry,
  QuoteConfirmLineInput,
  QuoteVendorCreate,
  QuoteDraftSummary,
  QuoteDraftFile,
} from './quoteApi';
import ScanDraftsPanel, { ScanDraftPages } from './PoScanDrafts';
import { formatCents } from './poApi';
import PoScanLineCard, {
  boardMint,
  draftMint,
  type ScanLineDraft,
  type ScanLineError,
} from './PoScanLineCard';
// The vendor form the Vendors admin page uses, mounted here as a drawer so a
// board line whose maker nobody has entered does not send the reviewer away.
import { VendorForm, type VendorFullRow } from '../Admin/VendorsAdmin';
import { ReviewNote, type ReviewSlot } from '../components/review/ReviewNote';
import { ConfidenceDot } from '../components/review/confidence';
import { laneInput as inputClass, laneLabel as labelClass } from '../components/review/fieldStyles';
import { groupSlots } from '../components/review/groupSlots';
import { ConfirmBar } from '../components/review/ConfirmBar';
import { VendorTypeOption } from './vendorTypeLabels';
// The one supplier picker. It fetches the active vendors itself, so this
// screen can offer "not that one, this one" without holding a list.
import VendorPicker, { type VendorPickerVendor } from '../components/VendorPicker';
import { skuIsWellFormed } from '../Parts/SkuBuilder';
import useItemKindsStore, { type ItemKind } from '../stores/useItemKindsStore';
import useCodedVendors, { codeOf, type CodedVendor } from '../Parts/useCodedVendors';
import type { Brand } from '../Parts/skuGrammar';
import type { MaterialType, CommodityClass } from '../Receiving/types';

/** The quote lane's reason tags. A superset of the import lane's. */
const QUOTE_REASON_LABELS: Record<string, string> = {
  low_confidence: "AI wasn't sure",
  unverified_text: 'Not found on the page',
  ungrounded_scan: 'Nothing could be checked',
  ai_remark: 'The reader left a note',
  model_not_sure: "AI wasn't sure",
  fee_line: 'Charged below the subtotal',
  currency_unstated: 'No currency printed',
  total_mismatch: "Total doesn't add up",
  subtotal_mismatch: "Subtotal doesn't add up",
  discount_inverted: 'Prices may be swapped',
};

// The same grammar vendors.code is validated against on the server.
const VENDOR_CODE = /^[A-Z]{2}$/;
// The id the not-yet-created supplier wears in the manufacturer list. Never
// sent anywhere: the segment carries the CODE, and the vendor exists by the
// time any line mints.
const PENDING_VENDOR_ID = '__pending_supplier__';

/**
 * Which brand this quote mints under.
 *
 * Read off the PARSE RESPONSE rather than the build's own instance: that is
 * server truth about which API answered, which is strictly better than a
 * build-time guess when the two could disagree.
 */
function quoteBrand(parsed: QuoteParseResponse): Brand {
  return parsed.instance === 'swarm' ? 'SD' : 'FF';
}

/**
 * Staged lines → editable drafts, with a KIND guessed for anything unmatched.
 *
 * The card is kind-first (see PoScanLineCard): the description's own type token
 * is looked up in the taxonomy, so a quoted grommet opens needing only a name
 * and the server assigns its short code at confirm, while a board-shaped guess
 * opens on the SKU builder with its segments filled. A token nothing recognizes
 * leaves the kind unanswered and the line asks. Fee lines never get a mint card:
 * nothing puts a bank charge on a shelf.
 *
 * `kinds` is passed rather than read here, so this stays a pure function of the
 * document plus the taxonomy; the caller awaits the load before hydrating.
 */
function draftLinesFrom(parsed: QuoteParseResponse, kinds: ItemKind[]): ScanLineDraft[] {
  const brand = quoteBrand(parsed);

  return parsed.extraction.lines.map((line, index) => {
    const matched = line.part_catalog_id !== null && line.part_catalog_id !== undefined;
    const description = line.description || '';
    let mint: ScanLineDraft['mint'] = null;

    if (!matched && line.line_type === 'stock' && !line.is_fee) {
      mint = draftMint({
        description,
        kinds,
        brand,
        // The reader's own code proposal on a line it could not match. A
        // board-shaped one names a kind, which beats a token guess.
        skuGuess: line.sku_guess,
        // Item identity off the document, prefilled and clearable. See
        // PoScanLineCard on why neither is ever derived silently.
        partNumber: line.part_number,
        manufacturer: line.manufacturer,
      });
    }

    return {
      key: `line-${index}`,
      lineNumber: line.line_number,
      description,
      // Carried whether or not this line mints, so switching a matched line to
      // "create a new item" reuses the read instead of starting blank.
      partNumber: line.part_number,
      manufacturer: line.manufacturer,
      quantity: line.quantity,
      unitCostCents: line.unit_cost_cents,
      unitPriceText: line.unit_price_text,
      unitPriceExact: line.unit_price_exact,
      lineType: line.line_type,
      included: line.included !== false,
      partCatalogId: line.part_catalog_id ?? null,
      matchedSku: line.sku_guess,
      mint,
    };
  });
}

export const QuoteDraftPage: React.FC = () => {
  const api = useQuoteApi();
  const rawApi = useApi();
  const toast = useToast();
  const history = useHistory();

  // The taxonomy a new line's kind is guessed against, and the assemblers a
  // board line's manufacturer segment can name. Read once here and handed down.
  const kinds = useItemKindsStore((s) => s.kinds);
  const fetchedVendors = useCodedVendors(rawApi);

  const [parsed, setParsed] = React.useState<QuoteParseResponse | null>(null);
  const [lines, setLines] = React.useState<ScanLineDraft[]>([]);
  const [reading, setReading] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [readError, setReadError] = React.useState<string | null>(null);
  const [vendorId, setVendorId] = React.useState<string | null>(null);
  // The ids the picker actually loaded (active vendors only), or null until it
  // has. The reader matches against EVERY vendor, retired ones included, because
  // the import lane it shares that resolver with files four-year-old paper. A
  // matched or suggested id the picker never loaded is therefore a retired
  // vendor, and confirm would refuse it (vendor_disabled) after the fact.
  const [knownVendorIds, setKnownVendorIds] = React.useState<Set<string> | null>(null);
  const [createVendor, setCreateVendor] = React.useState(false);
  const [vendorType, setVendorType] = React.useState('authorized_distributor');
  // The taxonomy is a table now (063), so the picker asks for it rather than
  // carrying a copy. Active only: this control CREATES a vendor, and offering a
  // retired type would be offering to put a new vendor somewhere retired.
  const [vendorTypes, setVendorTypes] = React.useState<VendorTypeOption[]>([]);
  // The two-letter code the new supplier will carry, or ''. Optional, because
  // most suppliers never appear in a SKU. Given one, the supplier is offered in
  // every board line's manufacturer segment BEFORE it exists: confirm creates
  // the vendor ahead of the mints, in the same transaction, so a board code
  // ending in this segment resolves to it. Without this, a quote from a new
  // maker for one of its own boards could not be finished on this screen.
  const [vendorCode, setVendorCode] = React.useState('');
  // Vendors created from a line card's "New vendor…" on this screen. They are
  // in the database already; this keeps them in the list without a refetch.
  const [addedVendors, setAddedVendors] = React.useState<CodedVendor[]>([]);
  // The same vendors as the Supplier picker needs them. Kept apart from
  // addedVendors because that list is only about board codes: a supplier with
  // no two-letter code cannot appear in a SKU and is still perfectly valid as
  // the one who sent this quote.
  const [drawerVendors, setDrawerVendors] = React.useState<VendorPickerVendor[]>([]);
  // Which line's manufacturer segment opened the vendor drawer, or null.
  const [vendorDrawerFor, setVendorDrawerFor] = React.useState<string | null>(null);
  /** reviewKey → how the person answered it. Absent means still open. */
  const [resolved, setResolved] = React.useState<Record<string, 'edited' | 'confirmed_as_staged'>>({});

  // ── the holding cell ─────────────────────────────────────────────────────
  // Every parse stages what it read plus the pages it read from (migration
  // 018), so walking away does not throw away a paid model call and a
  // half-finished review. `draftId` is the row this screen is currently working
  // from: confirm clears it on success and leaves it alone on failure.
  const [drafts, setDrafts] = React.useState<QuoteDraftSummary[]>([]);
  const [draftId, setDraftId] = React.useState<number | null>(null);
  const [draftFiles, setDraftFiles] = React.useState<QuoteDraftFile[]>([]);
  const [draftBusyId, setDraftBusyId] = React.useState<number | null>(null);

  const fileInput = React.useRef<HTMLInputElement | null>(null);

  const extraction: QuoteExtraction | null = parsed ? parsed.extraction : null;

  // ── the needs-review gate ─────────────────────────────────────────────────
  // The kit's grouping, shared with the catalog-import and packing-list lanes:
  // one amber note per place on the page, one "Looks right" clearing all of it.
  const slots = React.useMemo(
    () =>
      groupSlots(parsed?.review_required || [], {
        // A RING, not a border: the line cards it lands on carry no outline to
        // recolor, and a ring adds no layout so a flagged card stays put.
        ringClass: ' ring-2 ring-amber-500/60',
        isResolved: (key) => Boolean(resolved[key]),
        resolve: (key, resolution) => setResolved((r) => ({ ...r, [key]: resolution })),
      }),
    [parsed, resolved]
  );

  const slotFor = (entry: Pick<QuoteReviewEntry, 'target' | 'field' | 'line_number'>): ReviewSlot | null =>
    slots.get(reviewKey(entry)) || null;

  const openSlots = [...slots.values()].filter((s) => s.open);

  // ── who a board code may name ─────────────────────────────────────────────
  const createProposal = parsed?.vendor_resolution.create_proposal || null;
  const pendingCode = createVendor && createProposal && VENDOR_CODE.test(vendorCode) ? vendorCode : '';
  // Vendors that exist, retired ones included: fetched, plus any made from a
  // card's drawer. This is what a code on a SKU is judged against.
  const knownCodedVendors = React.useMemo(
    () => [...fetchedVendors, ...addedVendors.filter((a) => !fetchedVendors.some((f) => f.id === a.id))],
    [fetchedVendors, addedVendors]
  );
  // vendors_code_uniq is global, so a retired vendor's code is taken too.
  const codeTakenBy = pendingCode ? knownCodedVendors.find((v) => codeOf(v) === pendingCode) : null;
  // What the cards may OFFER: the active ones, plus the supplier about to be
  // created. A retired shop stays out of the picker and stays valid on a SKU.
  // A pending code somebody already holds is not offered: the blocker names
  // the collision, and a second option with the same value would only show it
  // as a duplicate.
  const codedVendors = React.useMemo(
    () =>
      pendingCode && createProposal && !codeTakenBy
        ? [...knownCodedVendors, { id: PENDING_VENDOR_ID, code: pendingCode, name: createProposal.name }]
        : knownCodedVendors,
    [knownCodedVendors, pendingCode, createProposal, codeTakenBy]
  );

  // ── what still blocks Create ──────────────────────────────────────────────
  const lineErrors = React.useMemo(() => {
    const out = new Map<string, ScanLineError>();
    // Boards only. A short kind's code is the server's to assign and is unique
    // by construction, so there is nothing here for two of them to collide over.
    const seenSku = new Map<string, number>();
    for (const line of lines) {
      if (!line.included || !line.mint) continue;
      // THE KIND IS THE FIRST QUESTION AND THE FIRST GATE. Nothing else about a
      // new item can be answered before it, and nothing here may invent one.
      if (!line.mint.kindKey) {
        out.set(line.key, {
          field: 'kind',
          message: `Line ${line.lineNumber} needs to say what sort of thing it is before it can be created.`,
        });
        continue;
      }
      if (line.mint.board) {
        const sku = (line.mint.sku || '').trim().toUpperCase();
        if (!skuIsWellFormed('board', sku)) {
          out.set(line.key, {
            field: 'sku',
            message: 'Finish the board code. Rev major and manufacturer code are both needed.',
          });
          continue;
        }
        if (seenSku.has(sku)) {
          out.set(line.key, {
            field: 'sku',
            message: `Line ${seenSku.get(sku)} is already creating ${sku}. Give this one a different code, or point it at that item.`,
          });
          continue;
        }
        // The segment has to name somebody. A code nobody carries mints a board
        // with no assembler and says nothing, which is how a supplier unticked
        // after its code was picked would otherwise slip through. Only judged
        // when the list loaded: with nothing to compare against, the segment is
        // the reviewer's word.
        const mfr = line.mint.board.vendorCode;
        if (
          knownCodedVendors.length > 0
          && mfr
          && mfr !== pendingCode
          && !knownCodedVendors.some((v) => codeOf(v) === mfr)
        ) {
          // The way out depends on which control is on screen: the Code field
          // only exists while Create is ticked.
          const wayOut = createVendor
            ? 'Give the new supplier that code, or pick another maker.'
            : createProposal
              ? "Tick Create for the quote's supplier and give them that code, or pick another maker."
              : 'Pick another maker, or add one with New vendor….';
          out.set(line.key, { field: 'sku', message: `${mfr} is nobody's code. ${wayOut}` });
          continue;
        }
        seenSku.set(sku, line.lineNumber);
      } else if (!line.mint.name.trim()) {
        // A short code says nothing about the thing, so the name is the only
        // thing any other screen will have to show.
        out.set(line.key, {
          field: 'name',
          message: 'Give this new item a name. It is what every screen will show.',
        });
        continue;
      }
      if (!line.mint.materialType) {
        out.set(line.key, { field: 'material_type', message: 'Pick a build stage. Nothing guesses it for you.' });
        continue;
      }
      if (!line.mint.commodityClass) {
        out.set(line.key, { field: 'commodity_class', message: 'Pick an item type. Nothing guesses it for you.' });
      }
    }
    return out;
  }, [lines, knownCodedVendors, pendingCode, createVendor, createProposal]);

  const includedLines = lines.filter((l) => l.included);
  const vendorRetired = !!vendorId && !!knownVendorIds && !knownVendorIds.has(vendorId);
  const vendorChosen = createVendor
    ? !!parsed?.vendor_resolution.create_proposal
    : !!vendorId && !vendorRetired;
  const blockers: string[] = [];
  if (includedLines.length === 0) blockers.push('Keep at least one line.');
  if (vendorRetired) blockers.push('That supplier is retired. Restore them under Vendors, or pick another supplier.');
  else if (!vendorChosen) blockers.push('Choose the supplier.');
  // Said here rather than at confirm, where it would come back as a bare
  // duplicate_code after everything else on the page passed. A one-letter code
  // mid-edit needs no sentence of its own: it is not sent, and any line that
  // followed it already reads "Finish the board code".
  if (codeTakenBy) {
    blockers.push(
      `${pendingCode} is already ${codeTakenBy.name}'s code${codeTakenBy.is_disabled ? ' (retired)' : ''}. Give the new supplier another.`
    );
  }
  if (lineErrors.size > 0) {
    blockers.push(
      `${lineErrors.size} line${lineErrors.size === 1 ? '' : 's'} `
      + `still ${lineErrors.size === 1 ? 'needs' : 'need'} an answer.`,
    );
  }
  if (openSlots.length > 0) {
    blockers.push(`${openSlots.length} thing${openSlots.length === 1 ? '' : 's'} to look at: ${openSlots.map((s) => s.label).join(', ')}.`);
  }

  // ── totals, recomputed from what is actually being ordered ────────────────
  // NOT the quote's printed figures: the reviewer can switch a line off or edit
  // a quantity, and a total that still showed the paper number would be the one
  // number on this screen that lies.
  const goodsCents = includedLines
    .filter((l) => l.lineType === 'stock')
    .reduce((sum, l) => sum + l.quantity * (l.unitCostCents || 0), 0);
  const feesCents = includedLines
    .filter((l) => l.lineType !== 'stock')
    .reduce((sum, l) => sum + l.quantity * (l.unitCostCents || 0), 0);

  const refreshDrafts = React.useCallback(async () => {
    try {
      const data = await api.listDrafts();
      setDrafts(data.drafts || []);
    } catch {
      // The list is a convenience on top of a flow that works without it. A
      // failed fetch must not stop somebody dropping a quote in.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    refreshDrafts();
  }, [refreshDrafts]);

  // The taxonomy, once. If the seeded default is not among the active types (an
  // admin retired it), fall to the first one rather than leaving the picker
  // showing a value the server will refuse at confirm.
  React.useEffect(() => {
    (async () => {
      try {
        const res = (await rawApi.get('/procurement/vendor-types')) as {
          types: (VendorTypeOption & { is_active: boolean })[];
        };
        const active = (res.types || []).filter((t) => t.is_active);
        setVendorTypes(active);
        if (active.length && !active.some((t) => t.key === vendorType)) {
          setVendorType(active[0].key);
        }
      } catch {
        // A failed load leaves the picker empty, and confirm already refuses a
        // create with no type. Nothing here should stop the quote being read.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Load one staged quote back onto the screen, exactly as parse left it. */
  function hydrate(staged: QuoteParseResponse, id: number | null, files: QuoteDraftFile[] = []) {
    setParsed(staged);
    // getState rather than the subscribed `kinds`: both callers await the load
    // immediately before this, and a closure would hold the array as it stood
    // when this render began.
    setLines(draftLinesFrom(staged, useItemKindsStore.getState().kinds));
    // Deliberately NOT restored: which flags were cleared is a judgment, and a
    // judgment made in another session by possibly another person is not one
    // this screen should inherit silently. The gate is cheap to re-clear and
    // expensive to get wrong.
    setResolved({});
    setDraftId(id);
    setDraftFiles(files);
    const matched = staged.vendor_resolution.matched_vendor_id;
    setVendorId(matched);
    setCreateVendor(!matched && !!staged.vendor_resolution.create_proposal);
    // The code belongs to the supplier this quote proposes; another quote's
    // supplier must not inherit it. Vendors created from a card's drawer stay
    // in the list, because they exist whichever quote is open.
    setVendorCode('');
  }

  async function openDraft(id: number) {
    setDraftBusyId(id);
    setReadError(null);
    try {
      const draft = await api.getDraft(id);
      // The kinds decide what every mint card opens as, so the list has to be
      // here before the lines are drafted. Cached, and a failure is recorded
      // rather than thrown: the cards then all ask.
      await useItemKindsStore.getState().load(rawApi);
      hydrate(draft.staged_doc, draft.id, draft.files);
    } catch (err: any) {
      setReadError(err?.data?.error?.message || err?.message || 'That quote could not be opened.');
      refreshDrafts();
    } finally {
      setDraftBusyId(null);
    }
  }

  async function discardDraft(id: number) {
    setDraftBusyId(id);
    try {
      await api.discardDraft(id);
      if (draftId === id) {
        setParsed(null);
        setLines([]);
        setDraftId(null);
        setDraftFiles([]);
      }
      await refreshDrafts();
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'That quote could not be discarded.');
    } finally {
      setDraftBusyId(null);
    }
  }

  async function readQuote(files: FileList | null) {
    if (!files || files.length === 0) return;
    setReading(true);
    setReadError(null);
    try {
      const result = await api.parse(Array.from(files));
      // Same reason as the draft path: the mint cards cannot be drafted against
      // a taxonomy that has not arrived.
      await useItemKindsStore.getState().load(rawApi);
      hydrate(result, result.draft_id ?? null, []);
      refreshDrafts();
    } catch (err: any) {
      // The lane's refusals (a EUR price list, a supplier that is us) arrive
      // here with a sentence worth reading, so it is shown rather than replaced.
      setReadError(err?.data?.error?.message || err?.message || 'That quote could not be read.');
      setParsed(null);
      setLines([]);
      setDraftId(null);
      setDraftFiles([]);
    } finally {
      setReading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  function patchLine(key: string, patch: Partial<ScanLineDraft>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function patchMint(key: string, patch: Partial<NonNullable<ScanLineDraft['mint']>>) {
    setLines((prev) =>
      prev.map((l) => (l.key === key && l.mint ? { ...l, mint: { ...l.mint, ...patch } } : l))
    );
  }

  // ── picking and creating are one decision ────────────────────────────────
  // Two controls, one answer. The confirm payload carries `vendor_create` OR
  // `vendor_id`, so leaving both set would be sending a state the server has no
  // reading for; each of these clears the other rather than letting the payload
  // builder pick a winner further down.
  function chooseVendor(id: string | null) {
    setVendorId(id);
    if (id) setCreateVendor(false);
  }

  function toggleCreateVendor(next: boolean) {
    setCreateVendor(next);
    if (next) setVendorId(null);
  }

  // The code follows onto every board line that already picked it, so changing
  // your mind about two letters is not a visit to each card. Mid-edit the lines
  // read one letter and say "Finish the board code", which clears itself when
  // the second letter lands.
  function changeVendorCode(raw: string) {
    const next = raw.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 2);
    const previous = pendingCode;
    setVendorCode(next);
    if (previous && previous !== next) {
      setLines((prev) =>
        prev.map((l) =>
          l.mint?.board && l.mint.board.vendorCode === previous
            ? { ...l, mint: { ...l.mint, ...boardMint({ ...l.mint.board, vendorCode: next }) } }
            : l
        )
      );
    }
  }

  async function createDraft() {
    if (!parsed || !extraction || blockers.length > 0) return;
    setCreating(true);
    try {
      const payloadLines: QuoteConfirmLineInput[] = lines.map((line) => ({
        description: line.description || null,
        quantity: line.quantity,
        line_type: line.lineType,
        unit_cost_cents: line.unitCostCents,
        included: line.included,
        part_catalog_id: line.partCatalogId,
        mint: line.mint
          ? {
              // The kind is what tells the server which code to make. A board
              // carries the composed string it was built from; a short kind
              // carries NO sku and the server assigns STEM-XXXX at mint.
              kind_key: line.mint.kindKey as string,
              name: line.mint.name.trim() || null,
              ...(line.mint.board ? { sku: (line.mint.sku || '').trim().toUpperCase() } : {}),
              material_type: line.mint.materialType as MaterialType,
              commodity_class: line.mint.commodityClass as CommodityClass,
              description: line.description || null,
              mpn: (line.mint.mpn || '').trim() || null,
              manufacturer: (line.mint.manufacturer || '').trim() || null,
            }
          : null,
      }));

      const proposal = parsed.vendor_resolution.create_proposal;
      const vendorCreate: QuoteVendorCreate | null =
        createVendor && proposal
          ? {
              name: proposal.name,
              type: vendorType,
              contact_name: extraction.vendor.contact_name,
              contact_email: extraction.vendor.contact_email,
              contact_phone: extraction.vendor.contact_phone,
              address_line1: extraction.vendor.address_line1,
              address_line2: extraction.vendor.address_line2,
              city: extraction.vendor.city,
              region: extraction.vendor.region,
              postal_code: extraction.vendor.postal_code,
              country: extraction.vendor.country,
              payment_terms: extraction.terms,
              ...(pendingCode ? { code: pendingCode } : {}),
            }
          : null;

      const result = await api.confirm({
        quote: {
          source_filename: parsed.source_filename,
          source: parsed.source,
          extraction,
          ...(vendorCreate ? { vendor_create: vendorCreate } : { vendor_id: vendorId }),
          lines: payloadLines,
        },
        ...(draftId ? { draft_id: draftId } : {}),
        staged_extraction: extraction,
        staged_confidence: parsed.confidence,
        staged_warnings: parsed.warnings,
        // ONLY what was actually answered. The obvious shortcut here is to map
        // every entry and default the unanswered ones to 'confirmed_as_staged' —
        // harmless today, because Create is disabled while anything is open, but
        // it makes the server's gate trivially satisfiable by its own client. If
        // that button ever stops being disabled, the gate should refuse rather
        // than wave through a claim this screen had no basis for making.
        reviews: (parsed.review_required || [])
          .filter((entry) => resolved[reviewKey(entry)])
          .map((entry) => ({
            target: entry.target,
            field: entry.field,
            line_number: entry.line_number,
            resolution: resolved[reviewKey(entry)],
          })),
      });

      toast.success(
        `${result.po_number} drafted${result.minted.length ? `, ${result.minted.length} new item${result.minted.length === 1 ? '' : 's'} created` : ''}. Submit it when you are ready to order.`
      );
      history.push(`/procurement/po/${result.purchase_order_id}`);
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'That draft could not be created.');
    } finally {
      setCreating(false);
    }
  }

  const documentSlot = slotFor({ target: 'document', field: null, line_number: null });

  // ── EVERY SLOT MUST HAVE SOMEWHERE TO BE CLEARED ─────────────────────────
  // Three targets exist and all three must render, or the gate deadlocks: an
  // entry with no "Looks right" button blocks Create forever and the screen
  // gives no way out. That is not hypothetical — the header fields carry
  // confidences, and ANY of them under 0.8 (vendor, terms, subtotal, currency,
  // the ship-to block) produces a field-target entry. Document and line slots
  // have obvious homes; these did not, and the omission was invisible until a
  // fixture happened to include one.
  //
  // Rendered as one block rather than threaded under each <dd>, because the set
  // is open: the server decides which fields it is unsure of, and a per-field
  // mount point is a list this screen would have to keep in step with it. The
  // entry's own label already names the field.
  const fieldSlots = [...slots.values()].filter(
    (slot) => slot.entries.length > 0 && slot.entries[0].target === 'field'
  );

  return (
    <PageLayout width="narrow" kind="workspace" className="pt-6 space-y-5">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold text-on-surface">Draft a PO from a quote</h1>
          <HelpDrawer title="About drafting from a quote">
            <p>
              A supplier sends a quotation. Drop it here and it is read once: the supplier,
              the line items, the prices, the terms and any discount. You check what it
              read, and it becomes a draft purchase order.
            </p>
            <p className="text-on-surface font-medium">It stops at a draft</p>
            <p>
              Nothing is ordered and nobody is asked to approve anything. The draft opens
              when this finishes, and submitting it is a separate step, with the same
              approval rules as an order you typed yourself.
            </p>
            <p className="text-on-surface font-medium">Discounts</p>
            <p>
              Where a quote shows a list price and a discounted one, the lines carry the
              discounted price, because that is what the order commits to. The list price
              and the discount are written into the purchase order's notes, so when the
              invoice arrives there is a record of why the numbers look the way they do.
            </p>
            <p className="text-on-surface font-medium">Shipping and bank charges</p>
            <p>
              Anything the quote charges below its subtotal is carried onto the order as a
              charge line, so the order total matches the quote instead of coming up short.
              Those lines never become stock, and each one asks you to look at it first.
              Drop any the supplier bills separately.
            </p>
            <p className="text-on-surface font-medium">New items</p>
            <p>
              A line nothing in the catalog matches offers to create an item. It needs a
              build stage and an item type, and neither is guessed for you. The supplier's
              part number is kept so the next quote for the same part matches instead of
              asking again. Clear it if that number is the distributor's rather than the
              manufacturer's.
            </p>
            <p>
              That short card is the quick way, and everything on the quote is created
              together when you create the draft. For a line that deserves a proper item,
              use &quot;Create with the wizard&quot; instead: the same form the parts
              catalog uses, with a name, a kind and a manufacturer you can add on the spot.
              It creates the item straight away rather than waiting for the draft.
            </p>
            <p className="text-on-surface font-medium">What it will not read</p>
            <p>
              Commercial quotations only, never drawings or specifications. A quote priced
              in anything other than US dollars is refused rather than guessed at, because
              nothing here records a currency. Enter those by hand.
            </p>
          </HelpDrawer>
        </div>
        <p className="text-sm text-on-surface-variant">
          Read a supplier's quotation and turn it into a draft purchase order. Nothing is
          ordered until you submit the draft.
        </p>
      </header>

      {/* ── upload ─────────────────────────────────────────────────────────── */}
      <Panel label="Upload a quote" className="max-w-3xl">
        <label className="block">
          <span className={labelClass}>The quote</span>
          <FileField
            ref={fileInput}
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            disabled={reading}
            onChange={(e) => readQuote(e.target.files)}
            aria-label="Choose the quote to read"
          />
        </label>
        <p className="text-xs text-on-surface-variant mt-2">
          One quote at a time: a PDF, or photos of its pages. Commercial quotations only,
          never drawings or specifications.
        </p>
        <Reveal show={reading}>
          <p className="text-sm text-primary mt-2">Reading the quote…</p>
        </Reveal>
        <Reveal show={!!readError}>
          <Alert tone="error" variant="text" className="mt-2">
            {readError}
          </Alert>
        </Reveal>
      </Panel>

      {/* ── waiting to be drafted ──────────────────────────────────────────────
          Shown only when nothing is open, so it is a way back in rather than a
          distraction beside a review in progress. Communal: a quote read by
          whoever got the supplier's email is drafted by whoever places orders. */}
      {!parsed && drafts.length > 0 && (
        <Panel label="Quotes waiting to be drafted" className="max-w-3xl space-y-2">
          <h2 className="text-sm font-medium text-on-surface">
            Waiting to be drafted{' '}
            <span className="text-xs text-on-surface-variant">({drafts.length})</span>
          </h2>
          <ScanDraftsPanel
            drafts={drafts}
            busyId={draftBusyId}
            onReview={openDraft}
            onDiscard={discardDraft}
            pageUrl={quoteDraftPageUrl}
            copy={{
              untitled: 'Unread quote',
              empty: 'Nothing is waiting. Quotes you read here stay until you draft or discard them.',
              discardQuestion: 'Throw this quote away? The reading and the pages go with it.',
            }}
          />
        </Panel>
      )}

      {parsed && extraction && (
        <>
          {/* ── the header the quote printed ──────────────────────────────── */}
          <Panel label="Quote header" className="space-y-3">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <h2 className="text-sm font-medium text-on-surface">
                Quote {extraction.quote_number || '(unnumbered)'}
              </h2>
              <div className="flex items-baseline gap-3">
                {/* How sure the reader was about this quote as a whole. Every
                    document in this lane is an AI reading, so it always shows.
                    self-center: this row aligns on the baseline, where a dot
                    with no text of its own would sit low. */}
                <ConfidenceDot confidence={parsed.confidence} className="self-center" />
                <span className="text-xs text-on-surface-variant">{parsed.source_filename}</span>
                {/* Closes the screen without touching the draft. Nothing is lost
                    by leaving: the reading is staged, so this and the browser
                    back button do the same thing. Present because a reviewer who
                    cannot see that is a reviewer who finishes work they meant to
                    put down. */}
                {draftId !== null && (
                  <button
                    type="button"
                    onClick={() => {
                      setParsed(null);
                      setLines([]);
                      setDraftId(null);
                      setDraftFiles([]);
                      refreshDrafts();
                    }}
                    className={buttonClass('quiet', 'sm')}
                  >
                    Finish this later
                  </button>
                )}
              </div>
            </div>

            {documentSlot && <ReviewNote slot={documentSlot} labels={QUOTE_REASON_LABELS} />}

            {/* The pages behind the reading. What makes a later review a REVIEW
                rather than a retype: a staged quote is a model's opinion about a
                document, and you cannot tell a misread part number from a real
                one with nothing to check it against. */}
            {draftId !== null && draftFiles.length > 0 && (
              <ScanDraftPages
                draftId={draftId}
                files={draftFiles}
                sourceFilename={parsed.source_filename}
                pageUrl={quoteDraftPageUrl}
                noun="quote"
              />
            )}

            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <dt className={labelClass}>Supplier</dt>
                <dd className="text-on-surface">{extraction.vendor.name || '—'}</dd>
              </div>
              <div>
                <dt className={labelClass}>Quoted</dt>
                <dd className="text-on-surface">{extraction.quote_date || '—'}</dd>
              </div>
              <div>
                <dt className={labelClass}>Expires</dt>
                <dd className="text-on-surface">{extraction.expires_on || '—'}</dd>
              </div>
              <div>
                <dt className={labelClass}>Terms</dt>
                <dd className="text-on-surface">{extraction.terms || '—'}</dd>
              </div>
            </dl>

            {fieldSlots.length > 0 && (
              <Alert tone="warning" live={false} title="Worth checking on this quote">
                <div className="space-y-2">
                  {fieldSlots.map((slot) => (
                    <div key={slot.key}>
                      <p className="text-[11px] text-on-surface-variant capitalize">{slot.label}</p>
                      <ReviewNote slot={slot} labels={QUOTE_REASON_LABELS} />
                    </div>
                  ))}
                </div>
              </Alert>
            )}

            {/* Discounts are shown here and WRITTEN TO THE PO NOTES on create.
                The line itself carries the net price, because that is what the
                order commits to and what receiving will cost stock at. */}
            {extraction.discounts.length > 0 && (
              <Panel label="Discounts on this quote" on="panel" padding="sm">
                <p className="text-xs font-medium text-on-surface-variant mb-1.5">Discounts on this quote</p>
                <ul className="space-y-1 text-xs text-on-surface-variant">
                  {extraction.discounts.map((d) => (
                    <li key={d.line_number}>
                      Line {d.line_number}
                      {d.description ? ` (${d.description})` : ''}: {d.discount_text || 'discount applied'}
                      {d.list_price_cents !== null && d.net_price_cents !== null && (
                        <>
                          {' '}
                          <span className="text-on-surface-variant line-through">{formatCents(d.list_price_cents)}</span>{' '}
                          <span className="text-on-surface">{formatCents(d.net_price_cents)}</span> each
                        </>
                      )}
                      {d.saved_cents ? <span className="text-green-400"> saves {formatCents(d.saved_cents)}</span> : null}
                    </li>
                  ))}
                </ul>
                <p className="text-[10px] text-on-surface-variant mt-2">
                  The lines below carry the discounted price. This note is saved onto the
                  purchase order.
                </p>
              </Panel>
            )}
          </Panel>

          {/* ── the supplier ───────────────────────────────────────────────── */}
          {/* WHAT THE READER FOUND IS A STARTING POINT, NOT THE ANSWER.
              Every state on this screen ends in the same picker: a match can be
              wrong, a near-miss suggestion needs one press to take, and a quote
              whose letterhead read as nothing still has a supplier a person
              knows. The old version stated its findings and offered no way to
              disagree with any of them, which left "Choose the supplier."
              blocking Create with nothing on screen that could clear it.

              Picking and creating are mutually exclusive, in both directions:
              the payload sends `vendor_create` OR `vendor_id`, never both. */}
          <Panel label="Supplier" className="space-y-3">
            <h2 className="text-sm font-medium text-on-surface">Supplier</h2>

            {parsed.vendor_resolution.matched_vendor_id && (
              <p className="text-sm text-on-surface-variant">
                Matched{' '}
                <span className="text-on-surface">{parsed.vendor_resolution.matched_name}</span>
                <span className="text-xs text-on-surface-variant"> ({parsed.vendor_resolution.match_kind})</span>
              </p>
            )}

            {!parsed.vendor_resolution.matched_vendor_id
              && !parsed.vendor_resolution.create_proposal && (
              <Alert tone="error" variant="text" live={false}>No supplier could be read from this quote.</Alert>
            )}

            {/* The field shows the matched vendor at rest when that vendor is
                in the list the picker loaded. It reads empty while `Create …` is
                ticked (nothing is chosen then) and when the match is a RETIRED
                vendor, which the callout under it explains and the blocker
                refuses; the picker only lists active vendors. */}
            {/* Picking and creating clear each other, and neither control says
                so to a screen reader: the picker's name stays "Supplier" whether
                it just lost its value or never had one. One sentence of state
                that changes when the decision does. */}
            <p className="sr-only" role="status">
              {createVendor && parsed.vendor_resolution.create_proposal
                ? `Creating ${parsed.vendor_resolution.create_proposal.name} as a new supplier.`
                : vendorRetired
                  ? 'The matched supplier is retired.'
                  : vendorId
                    ? 'Supplier chosen.'
                    : 'No supplier chosen.'}
            </p>
            <VendorPicker
              id="quote-vendor"
              label="Supplier"
              className="max-w-sm"
              value={vendorId}
              extraVendors={drawerVendors}
              onVendorsLoaded={(vs) => setKnownVendorIds(new Set(vs.map((v) => v.id)))}
              onSelect={(v) => chooseVendor(v ? v.id : null)}
              placeholder="Search suppliers"
              labelClassName={labelClass}
              inputClassName={inputClass}
            />

            <Reveal show={vendorRetired}>
              <Alert tone="warning" variant="text" live={false}>
                {parsed.vendor_resolution.matched_name || 'That supplier'} is retired, so no new
                order can be placed with them. Restore them under Vendors, or pick another
                supplier above.
              </Alert>
            </Reveal>

            {/* Retired near-misses are dropped rather than offered: a chip that
                lands in the refusal above is worse than no chip. And once a
                supplier is chosen the chips have done their job: the question
                "did you mean" has been answered, whichever way. */}
            {!parsed.vendor_resolution.matched_vendor_id
              && !vendorId
              && parsed.vendor_resolution.suggestions.some((s) => !knownVendorIds || knownVendorIds.has(s.vendor_id)) && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-on-surface-variant">Did you mean</span>
                {parsed.vendor_resolution.suggestions
                  .filter((s) => !knownVendorIds || knownVendorIds.has(s.vendor_id))
                  .map((s) => (
                  <button
                    key={s.vendor_id}
                    type="button"
                    onClick={() => chooseVendor(s.vendor_id)}
                    className={buttonClass('quiet', 'sm')}
                  >
                    Use {s.name}
                  </button>
                ))}
              </div>
            )}

            {!parsed.vendor_resolution.matched_vendor_id
              && parsed.vendor_resolution.create_proposal && (
              <div className="space-y-2">
                <Checkbox
                  checked={createVendor}
                  onChange={(e) => toggleCreateVendor(e.target.checked)}
                  label={<>Create <span className="font-medium">{parsed.vendor_resolution.create_proposal.name}</span> as a new supplier</>}
                />
                <Reveal show={createVendor}>
                  <div className="flex flex-wrap items-start gap-3 p-0.5">
                    <label className="block w-full max-w-xs">
                      <span className={labelClass}>Supplier type</span>
                      <Select
                        label="Supplier type"
                        options={vendorTypes.map((t) => ({ key: t.key, label: t.label }))}
                        value={vendorType}
                        onChange={setVendorType}
                      />
                      <p className="text-[10px] text-on-surface-variant mt-1">
                        No quote states this, so nothing guesses it.
                      </p>
                    </label>
                    {/* Optional, and the reason a maker quoting its own board can
                        be finished here: with a code, the supplier is offered in
                        the Mfr segment of every board line below before it
                        exists. */}
                    <label className="block w-full max-w-xs">
                      <span className={labelClass}>Code</span>
                      <Field
                        thumb
                        className="font-mono"
                        value={vendorCode}
                        maxLength={2}
                        onChange={(e) => changeVendorCode(e.target.value)}
                        placeholder="··"
                        aria-label="Supplier code"
                        aria-describedby="quote-supplier-code-hint"
                      />
                      <p id="quote-supplier-code-hint" className="text-[10px] text-on-surface-variant mt-1">
                        Optional. Two letters, and this supplier can be the Mfr of a board code below.
                      </p>
                    </label>
                  </div>
                </Reveal>
              </div>
            )}
          </Panel>

          {/* ── the lines ──────────────────────────────────────────────────── */}
          <section className="space-y-2">
            <h2 className="text-sm font-medium text-on-surface">
              Lines <span className="text-xs text-on-surface-variant">({includedLines.length} of {lines.length} kept)</span>
            </h2>
            {lines.map((line) => {
              const slot = slotFor({ target: 'line', field: null, line_number: line.lineNumber });
              // The flagged line wears the amber ring while its slot is open,
              // the same ring the catalog-import and packing-list lanes put on
              // their rows: on all three the doubt is about the ROW, not one
              // field in it. `slot.ring` is '' once the slot resolves, so
              // answering the note un-rings the card in the same render as the
              // note turning green.
              return (
                <div key={line.key} className="space-y-1">
                  <PoScanLineCard
                    line={line}
                    error={lineErrors.get(line.key) || null}
                    flagClass={slot?.ring || ''}
                    onPatch={(patch) => {
                      patchLine(line.key, patch);
                      slot?.markEdited();
                    }}
                    onPatchMint={(patch) => patchMint(line.key, patch)}
                    // Switching a MATCHED line to mint: the read's own
                    // suggestions are still the best starting point, and
                    // dropping them here would silently punish changing your
                    // mind. Same helper, same guesses, as an unmatched line.
                    onSwitchToMint={() => {
                      patchLine(line.key, {
                        partCatalogId: null,
                        matchedSku: null,
                        matchedItem: null,
                        mint: draftMint({
                          description: line.description,
                          kinds,
                          brand: quoteBrand(parsed),
                          partNumber: line.partNumber,
                          manufacturer: line.manufacturer,
                        }),
                      });
                    }}
                    onSwitchToMatch={() => patchLine(line.key, { mint: null })}
                    codedVendors={codedVendors}
                    onNewVendor={() => setVendorDrawerFor(line.key)}
                    brand={quoteBrand(parsed)}
                  />
                  {slot && (
                    <div className="pl-7">
                      <ReviewNote slot={slot} labels={QUOTE_REASON_LABELS} />
                    </div>
                  )}
                </div>
              );
            })}
          </section>

          {/* ── what this order will say ───────────────────────────────────── */}
          <Panel label="This order" className="space-y-2">
            <h2 className="text-sm font-medium text-on-surface">This order</h2>
            <dl className="space-y-1 text-sm">
              <div className="flex justify-between">
                <dt className="text-on-surface-variant">Goods</dt>
                <dd className="text-on-surface">{formatCents(goodsCents)}</dd>
              </div>
              {feesCents > 0 && (
                <div className="flex justify-between">
                  <dt className="text-on-surface-variant">Freight and charges</dt>
                  <dd className="text-on-surface">{formatCents(feesCents)}</dd>
                </div>
              )}
              <div className="flex justify-between border-t border-outline-variant pt-1 font-medium">
                <dt className="text-on-surface-variant">Total</dt>
                <dd className="text-on-surface">{formatCents(goodsCents + feesCents)}</dd>
              </div>
              <Reveal show={extraction.printed_total_cents !== null && extraction.printed_total_cents !== goodsCents + feesCents}>
                <p className="text-xs text-amber-300/90 pt-1">
                  The quote printed {formatCents(extraction.printed_total_cents ?? 0)}. This order differs
                  because of what you changed above.
                </p>
              </Reveal>
            </dl>
          </Panel>

          {/* ── create ─────────────────────────────────────────────────────── */}
          {/* The blockers are announced here and nowhere else on this page, so
              this is the one bar of the four that asks for role="status". */}
          <ConfirmBar
            mode="static"
            className="space-y-2"
            blockers={blockers}
            statusRole
            label="Create the draft"
            busyLabel="Creating…"
            busy={creating}
            disabled={creating || blockers.length > 0}
            onConfirm={createDraft}
            afterButton={
              <p className="text-xs text-on-surface-variant">
                This creates a draft purchase order. Nothing is sent to the supplier until you
                submit it.
              </p>
            }
          />
        </>
      )}

      {/* ── a maker nobody has entered ─────────────────────────────────────── */}
      {/* One drawer for the page, opened from any board card's "New vendor…".
          A card in a list of thirty is nowhere to put a form, and the Supplier
          section above is about who SENT the quote, which on a distributor's
          quote is not who builds the board. The saved vendor goes into the list
          every card reads, into the segment that asked for it, and into the
          Supplier picker above, since on a maker's own quote it is both. */}
      {vendorDrawerFor && (
        <VendorForm
          mode="create"
          types={vendorTypes}
          onClose={() => setVendorDrawerFor(null)}
          onSaved={(saved?: VendorFullRow) => {
            const forLine = vendorDrawerFor;
            setVendorDrawerFor(null);
            if (!saved) return;
            // Offered as the SUPPLIER straight away, code or no code: the row
            // is in the database, and the picker fetched before it existed.
            setDrawerVendors((prev) =>
              prev.some((v) => v.id === saved.id)
                ? prev
                : [...prev, {
                  id: saved.id,
                  code: saved.code,
                  name: saved.name,
                  type: saved.type,
                  on_hold: saved.on_hold,
                  release_date: saved.release_date,
                  is_disabled: saved.is_disabled,
                }]
            );
            // The vendor exists either way; the code is what a board code can
            // carry. Saying so beats a drawer that closes and changes nothing.
            if (!saved.code) {
              toast.error(
                `${saved.name} was created, but a board code carries a two-letter vendor code `
                + 'and this one has none. Add one under Vendors, then pick it here.'
              );
              return;
            }
            const code = saved.code.toUpperCase();
            setAddedVendors((prev) =>
              prev.some((v) => v.id === saved.id)
                ? prev
                : [...prev, { id: saved.id, code, name: saved.name, is_disabled: !!saved.is_disabled }]
            );
            setLines((prev) =>
              prev.map((l) =>
                l.key === forLine && l.mint?.board
                  ? { ...l, mint: { ...l.mint, ...boardMint({ ...l.mint.board, vendorCode: code }) } }
                  : l
              )
            );
          }}
        />
      )}
    </PageLayout>
  );
};

export default QuoteDraftPage;
